import type { Analytics } from "@segment/analytics-next";
import React, { FC, PropsWithChildren, createContext, useEffect, useState } from "react";

const AnalyticsContext = createContext<Analytics | null>({} as any);

interface Props extends PropsWithChildren {
  segmentWriteKey: string;
}

const AnalyticsProvider: FC<Props> = ({ segmentWriteKey, children }) => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);

  useEffect(() => {
    let active = true;
    setAnalytics(null);
    const loadAnalytics = async () => {
      try {
        // Loaded on demand so the SDK isn't part of the code every page waits
        // for before it can render.
        const { AnalyticsBrowser } = await import("@segment/analytics-next");
        const [response] = await AnalyticsBrowser.load({ writeKey: segmentWriteKey });
        if (active) setAnalytics(response);
      } catch (error) {
        // Analytics is optional: keep children usable when the SDK cannot load.
        if (active) console.warn("Analytics unavailable; continuing without tracking", error);
      }
    };
    void loadAnalytics();
    return () => {
      active = false;
    };
  }, [segmentWriteKey]);

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};

const useAnalytics = () => React.useContext(AnalyticsContext);

export { AnalyticsProvider, useAnalytics };
